import { useMemo, useState } from "react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { inr, sign } from "@/lib/api";
import { Side, Stat, TradesTable } from "@/components/Bits";
import DayReplay from "@/components/DayReplay";

export default function RunReport({ run }) {
  const s = run.summary;
  const [day, setDay] = useState(null);
  const trades = useMemo(() => run.days.flatMap((d) => d.trades.map((t) => ({ ...t, date: d.date }))), [run]);
  const selDay = run.days.find((d) => d.date === day);
  return (
    <div className="space-y-4" data-testid="run-report">
      <div className="text-xs text-slate-400 num">{run.date_from} → {run.date_to} · {run.source === "sample" ? "SAMPLE data" : "Kite data"} · stop {run.settings.stop_mode} · sizing {run.settings.sizing}</div>
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2">
        <Stat label="Net P&L" value={inr(s.net)} cls={sign(s.net)} sub={`charges ${inr(s.charges)}`} testId="bt-stat-net" />
        <Stat label="Trades" value={s.trades} sub={`${s.trade_days}/${s.days} days traded`} testId="bt-stat-trades" />
        <Stat label="Win rate" value={`${s.win_rate}%`} testId="bt-stat-winrate" />
        <Stat label="Avg R" value={s.avg_r} cls={sign(s.avg_r)} testId="bt-stat-avgr" />
        <Stat label="Profit factor" value={s.profit_factor ?? "—"} testId="bt-stat-pf" />
        <Stat label="Max drawdown" value={inr(s.max_dd)} cls="down" sub={`${s.max_dd_pct}% of capital`} testId="bt-stat-dd" />
        <Stat label="Gross" value={inr(s.gross)} cls={sign(s.gross)} testId="bt-stat-gross" />
      </div>
      <div className="panel p-3" data-testid="equity-curve">
        <div className="label-xs mb-2">Equity curve (net, ₹)</div>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={s.curve} margin={{ left: 8, right: 8, top: 4, bottom: 0 }}>
              <defs><linearGradient id="eq" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stopColor="#06B6D4" stopOpacity={0.35} /><stop offset="100%" stopColor="#06B6D4" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid stroke="#1E293B" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 10 }} minTickGap={30} />
              <YAxis tick={{ fill: "#64748B", fontSize: 10 }} width={60} />
              <Tooltip contentStyle={{ background: "#111726", border: "1px solid #334155", fontSize: 12 }} />
              <Area type="monotone" dataKey="equity" stroke="#06B6D4" fill="url(#eq)" strokeWidth={1.5} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="grid xl:grid-cols-[380px_1fr] gap-4">
        <div className="panel p-3" data-testid="days-list">
          <div className="label-xs mb-2">Days · tap for replay</div>
          <div className="max-h-[560px] overflow-y-auto">
            {run.days.map((d) => (
              <button key={d.date} onClick={() => setDay(d.date)} data-testid={`day-row-${d.date}`}
                className={`w-full text-left px-2 py-2 border-b border-[#1E293B]/60 text-xs hover:bg-[#172033] transition-colors ${day === d.date ? "bg-[#172033]" : ""}`}>
                <div className="flex items-center gap-2">
                  <span className="num text-slate-300">{d.date}</span><Side side={d.bias} />
                  <span className="text-slate-400 truncate">{d.sector}</span>
                  <span className={`ml-auto num ${sign(d.net)}`}>{d.trades.length ? inr(d.net) : "—"}</span>
                </div>
                {d.reason && <div className="text-[11px] text-amber-300/80 mt-0.5 truncate">{d.reason}</div>}
              </button>
            ))}
          </div>
        </div>
        <div className="min-w-0">
          {selDay ? <DayReplay key={selDay.date} day={selDay} /> : (
            <div className="panel p-3"><div className="label-xs mb-2">All trades</div><TradesTable trades={trades} showDate testId="bt-trades" targetLabel="2R" onRow={(t) => setDay(t.date)} /></div>
          )}
        </div>
      </div>
    </div>
  );
}
